<template>
    <div class="header-search-box">
        <form-select
            class="header-search-box__select"
            :selected-value="selectedValue"
            :options="options"
            :autocomplete="true"
            :highlight="true"
            :placeholder="placeholder"
            :rows="10"
            :fixed-direction="FormSelectFixedDirection.Down"
            @input="selectedValue = $event"
            @change="handleSelect"
            @search="handleSearch"
        ></form-select>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import { getProductSearchResults } from '@/api/search/header-search.api';
    import FormSelect from '@/components/base/FormSelect.vue';
    import {
        FormSelectFixedDirection,
        FormSelectOption,
    } from '@/types/components/base-components.types';

    // 商品搜尋 API 回傳的單筆分類資訊
    interface SearchProductClass {
        label: string;
        SuperClassID: string;
        MainClassID: string;
        SubMainClassID: string;
        ProductClassID: string;
    }

    // 走查價模式的平台參數，與原本 header 搜尋一致
    const SEARCH_PLATFORM = 'PriceCalculator';
    // 還原網址 q 參數預填時，暫存選項使用的固定 value
    const INITIAL_QUERY_VALUE = '__header_search_initial_query__';

    // ----- props -----
    withDefaults(
        defineProps<{
            placeholder?: string;
        }>(),
        {
            placeholder: '請輸入商品關鍵字',
        }
    );

    // ----- state -----
    const selectedValue = ref<string | number>('');
    const options = ref<FormSelectOption[]>([]);
    // 以「平台_關鍵字」為 key 快取搜尋結果，避免重複請求
    const searchCache = ref<Record<string, SearchProductClass[]>>({});
    // 以選項 value 對應原始搜尋結果，供選取後組出導頁網址
    const resultMap = ref<Map<string | number, SearchProductClass>>(new Map());

    // ----- methods -----
    // 選取建議項目後，依商品分類資訊導向對應頁面
    const handleSelect = (value: string | number): void => {
        const item = resultMap.value.get(value);
        if (!item) return;

        navigateToProduct(item);
    };

    // autocomplete 搜尋文字變更時，查詢商品並更新下拉選項
    const handleSearch = async (keyword: string): Promise<void> => {
        const term = keyword.trim();
        if (term === '') {
            options.value = [];
            resultMap.value = new Map();
            return;
        }

        const results = await fetchProductSearch(term);
        applySearchResults(results);
    };

    // 依商品分類組出網址並導頁，規則與原本 header 搜尋一致
    const navigateToProduct = (item: SearchProductClass): void => {
        const superClassId = (item.SuperClassID || '').trim();
        const mainClassId = (item.MainClassID || '').trim();
        const subMainClassId = (item.SubMainClassID || '').trim();

        // 有 MainClassID 走 super/main 路徑，否則只走 super 路徑
        let productUrl = mainClassId
            ? `/ProductCategory/ProductsClassification/${encodeURIComponent(superClassId)}/${encodeURIComponent(mainClassId)}`
            : `/ProductCategory/ProductsClassification/${encodeURIComponent(superClassId)}`;

        // 同時具備 Main/SubMain 時，SubMainClassID 以 query 傳遞
        if (mainClassId && subMainClassId) {
            productUrl += `?SubMainClassID=${encodeURIComponent(subMainClassId)}`;
        }

        window.location.href = `https://${window.location.host}${productUrl}`;
    };

    // 查詢商品搜尋結果，命中快取時直接回傳
    const fetchProductSearch = async (
        term: string
    ): Promise<SearchProductClass[]> => {
        const cacheKey = `${SEARCH_PLATFORM}_${term}`;
        if (cacheKey in searchCache.value) {
            return searchCache.value[cacheKey];
        }

        try {
            const response = await getProductSearchResults<SearchProductClass>(
                term,
                SEARCH_PLATFORM
            );

            const data = (response.data || []).map((item) => ({
                ...item,
                label: stripHtmlTags(item.label),
            }));
            searchCache.value[cacheKey] = data;
            return data;
        } catch (error) {
            console.error('商品搜尋請求失敗：', error);
            return [];
        }
    };

    // 將搜尋結果轉成下拉選項，並建立 value 對應原始資料的查找表
    const applySearchResults = (results: SearchProductClass[]): void => {
        const nextResultMap = new Map<string | number, SearchProductClass>();

        options.value = results.map((item, index) => {
            nextResultMap.set(index, item);
            return {
                value: index,
                label: item.label,
            };
        });
        resultMap.value = nextResultMap;
    };

    // 去除字串中的 HTML 標籤
    const stripHtmlTags = (text: string): string => {
        return text.replace(/<.*?>/g, ' ');
    };

    // 取得網址查詢字串參數值
    const getQueryParam = (name: string): string => {
        return new URL(window.location.href).searchParams.get(name) || '';
    };

    // ----- lifecycle -----
    onMounted(() => {
        // 還原原本以網址 q 參數預填搜尋字串的行為，
        // 以合成選項讓 FormSelect 將輸入框內容對應為該關鍵字
        const initialQuery = getQueryParam('q');
        if (initialQuery === '') return;

        options.value = [
            {
                value: INITIAL_QUERY_VALUE,
                label: initialQuery,
            },
        ];
        selectedValue.value = INITIAL_QUERY_VALUE;
    });
</script>

<style lang="scss" scoped>
    .header-search-box {
        position: relative;
        width: 30%;

        @media (max-width: 740px) {
            width: 100%;
        }

        :deep(.form-group-select) {
            min-height: 1px;
            padding: 4px;
            color: inherit;
            border: 1px solid $grayColorE7;
            border-radius: $border-radius-full;
            line-height: 2.5rem;

            &:hover:not(.disabled),
            &.open:not(.disabled) {
                background-color: #ffffff;
                border-color: $grayColorE7;
            }
        }

        // 右側的圓形搜尋按鈕
        :deep(.form-group-select)::after {
            content: '';
            top: 50%;
            right: 8px;
            width: 32px;
            height: 31px;
            border: 0;
            border-radius: 50%;
            background-color: $blueColor_2C94FF;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: center;
            background-size: 55%;
            transform: translateY(-50%);
        }

        // 搜尋輸入框：靠左對齊並保留右側按鈕空間
        :deep(.form-group-select) input.selected-text {
            width: 100%;
            height: 1.5rem;
            margin: 0;
            padding: 0 52px 0 16px;
            background-color: transparent;
            color: $textColor45;
            font-size: $font-size-sm;
            line-height: 1.5rem;
        }

        :deep(.form-group-select) .dropdown-options {
            z-index: 11;
        }

        // 每格高度依內容自動撐高，長商品名稱換行顯示，與 CoEditionOrderV2 一致
        :deep(.form-group-select) .dropdown-scroll-container li {
            height: auto;
            min-height: 32px;
            margin: 5px 0;
            font-size: $font-size-xs;
            line-height: 1.2rem;
            white-space: normal;
            word-break: break-all;
        }
    }
</style>
